<!-- eslint-disable indent -->
<template>
    <div>
        <MainModal
            title="Send rate card to client" 
            :show-modal="showModal" 
            :footer="view === 'email'"
            :close-btn="false" 
            size="md"
            @close="closeModal"
        >
            <template #body>
                <div>
                    <template v-if="view === 'templates'">
                        <div class="overflow-y-scroll max-h-600">
                            <div class="flex align-items-cente mb--10">
                                <BaseButton 
                                    variant="ghost"
                                    prefix-icon
                                    label="Go back"
                                    medium
                                    icon-name="chevron-left"
                                    class="p--0 text-medium"
                                    @submit="view = 'email'" 
                                />
                            </div>

                            <FormLabel class="text--base" title="Templates give you a preview on the layout of the PDF file you get when you download or send a rate card. Select a template below to continue." />

                            <div class="mt--20 rc__download__template">
                                <div class="grid gap-3 rc__template__list">
                                    <div 
                                        v-for="(temp, index) in downloadTemplates"
                                        :key="index"
                                        class="rc__template__item pointer"
                                        :class="{ 'active': temp.id === currentTemplate.id }"
                                        @click="setTemplate(temp)"
                                    >
                                        <div class="rc__template__item__img">
                                            <img :src="renderFilePath(temp.name)" class="d-block" :alt="`Download template for ${temp.id}`">
                                        </div>
    
                                        <div class="rc__template__item__text">
                                            <p>{{ temp.name }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>

                    <template v-else>
                        <Alert type="primary" classes="text--sm">
                            <template #text>
                                <p class="text--xs">
                                    Use this option to share this rate card as an email with clients using a pre-written message template.
                                </p>
                            </template>
                        </Alert>
    
                        <form ref="formRef" :class="{ 'rc__form': formHeight >= 300 }" @submit.prevent="">
                            <div class="mb--20 flex-1">
                                <FormLabel title="Select a client" html-for="rateType" />
                                <CustomDropdown 
                                    id="clientsDropdown" 
                                    v-model="client" 
                                    :options="clients"
                                    list-type="client" 
                                    index-label="name"
                                    :loading="fetchClientsLoading"
                                    :fetch-list-on-show="!clients.length"
                                    @search="handleFetchClients"
                                    @select="handleSelectClient" 
                                    @fetch-list-show="handleClientsDropdown"
                                />
                            </div>
    
                            <div class="mb--0 flex-1">
                                <div class="flex justify-content-between align-items-center">
                                    <FormLabel 
                                        title="Select rate card template" 
                                        html-for="rateType"
                                        info-type="info" 
                                    />
                                    <BaseButton 
                                        label="View templates" 
                                        class="p--0"
                                        prefix-icon
                                        icon-name="show"
                                        medium
                                        variant="ghost"
                                        @submit="view = 'templates'"
                                    />
                                </div>
                                <CustomDropdown 
                                    id="clientsDropdown" 
                                    v-model="currentTemplate" 
                                    :options="downloadTemplates"
                                    list-type="templates" 
                                    index-label="name"
                                    :searchable="false"
                                    readonly
                                />
                            </div>
    
                            <div class="mt--20 mb--5 flex align-items-center justify-content-between">
                                <FormLabel title="What the client sees:" class="mb--0" html-for="subject" />
                                <div class="flex align-items-center">
                                    <BaseButton 
                                        :label="isSuggestion ? '+ Use suggestion' : '- Use custom text'" 
                                        variant="ghost" 
                                        variant-style="primary"
                                        size="sm"
                                        @submit="isSuggestion = !isSuggestion"
                                    />
                                </div>
                            </div>
    
                            <template v-if="!isSuggestion">
                                <div class="rc__preview grid gap-2">
                                    <div class="rc__preview__text flex align-items-center">
                                        <span class="text--medium">Subject:</span>
                                        <span class="ml--5">{{ `Rate card for ${item.title}` }}</span>
                                    </div>
         
                                    <div class="rc__preview__text flex align-items-center justify-content-between">
                                        <div class="flex align-items-center">
                                            <span class="text--medium">To:</span>
                                            <span class="ml--5">{{ client.email }}</span>
                                        </div>
                                        <div 
                                            v-if="showAddCCEmailBtn"
                                            class="rounded-full p-1 flex align-items-center justify-content-center pointer" 
                                            style="width: 16px; height: 16px; border: 1px solid #64748b;" 
                                            @click="addCCEmails"
                                        >
                                            <span class="text--medium text-color-inherit">+</span>
                                        </div>
                                    </div>
         
                                    <div v-if="clientCCEmails.length" class="flex flex-wrap align-items-center rc__preview__text justify-content-between">
                                        <div class="flex align-items-center">
                                            <p class="text--medium">
                                                cc:
                                            </p>
                                            <div class="flex align-items-center">
                                                <p v-for="(email, index) in clientCCEmails" :key="email" class="rc__preview__text ml--5">
                                                    {{ email }}{{ index + 1 >= clientCCEmails.length ? '' : ',' }}
                                                </p>
                                            </div>
                                        </div>
                                        <div 
                                            class="rounded-full p-1 flex align-items-center justify-content-center pointer"
                                            style="width: 16px; height: 16px; border: 1px solid #64748b;" 
                                            @click="removeCCEmails"
                                        >
                                            <IconSvg name="close" width="12px" height="12px" />
                                        </div>
                                    </div>
         
                                    <p class="rc__preview__text">
                                        Hello {{ client.name }},
                                    </p>
         
                                    <p class="rc__preview__text">
                                        Please find attached my rate card outlining service offerings and pricing structure as requested.
                                    </p>
         
                                    <p class="rc__preview__text">
                                        These rates reflect my professional expertise and the quality of work I deliver to clients. Please review the attached information and let me know if you have any questions.
                                    </p>
         
                                    <p class="rc__preview__text">
                                        I would love to discuss how these services align with your project requirements and look forward to potential collaboration.
                                    </p>
         
                                    <p class="rc__preview__text mb--0">
                                        Best regards,
                                    </p>
                                             
                                    <p class="rc__preview__text mb--0">
                                        {{ userProfile.fullName }}
                                    </p>
                                </div>
                            </template>

                            <template v-else>
                                <div class="form__textarea__group pb--15 pl--15">
                                    <TipTap v-model="form.noteValue" class="pl--5" />
                                </div>
                            </template>
                        </form>
                    </template>
                </div>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <Base-Button
                        label="Cancel" 
                        variant="outline" 
                        :disabled="loading"
                        @submit="closeModal"
                    />
                    <Base-Button 
                        label="Send email"
                        class="ml--5"
                        :loading="loading" 
                        type="submit" 
                        :disabled="!client._id"
                        @submit="onComplete" 
                    />
                </div>
            </template>
        </MainModal>
    </div>
</template>

<script>
import FormLabel from "../../forms/FormLabel.vue";
import BaseButton from "../../buttons/BaseButton.vue";
import MainModal from "../MainModal.vue";
import CustomDropdown from "../../dropdowns/CustomDropdown.vue";
import Alert from "../../alert/Alert.vue";
import IconSvg from "../../icons/Icon-Svg.vue";
import Callouts from "../../alert/Callouts.vue";
import { assembleQueryList } from "@/utils/helpers";
import TipTap from "../../editor/TipTap.vue";

export default {
    name: "SendRateCard",

    components: { 
        MainModal, 
        BaseButton, 
        FormLabel,
        CustomDropdown,
        Alert,
        IconSvg,
        TipTap,
    },

    props: {
        showModal: {
            type: Boolean,
            default: false,
        },

        loading: {
            type: Boolean,
            default: false,
        },

        item: {
            type: Object,
            default: () => {},
        },
    },

    emits: ["cancel", "submit"],

    data() {
        return {
            formHeight: 0,
            resizeObserver: null,
            downloadTemplates: [
                { id: "template11", name: "Template1.1" },
                { id: "template12", name: "Template1.2" },
                { id: "template21", name: "Template2.1" },
                { id: "template22", name: "Template2.2" },
            ],
            clientCCEmails: [],
            currentTemplate: {
                id: "template11", 
                name: "Template1.1", 
            },
            view: "email",
            client: {},
            fetchClientsLoading: false,
            fetchClientsLoadingErr: false,
            step: 0,
            clients: [],
            isSuggestion: false,
            form: {
                noteValue: "",
            },
        };
    },

    computed: {
        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        appClients() {
            return this.$store.getters["clients/clients"];
        },

        userProfile() {
            return this.$store.getters["auth/profile"];
        },

        disableNextSteps() {
            return this.step + 1 >= this.downloadTemplates.length;
        },

        showAddCCEmailBtn() {
            return this.client?.ccEmails?.length && !this.clientCCEmails.length;
        },
    },

    mounted() {
        // Set up the ResizeObserver to watch the form height
        this.resizeObserver = new ResizeObserver(this.onResize);
        
        // Observe the form element
        if (this.$refs.formRef) {
            this.resizeObserver.observe(this.$refs.formRef);
            // Get initial height
            this.formHeight = this.$refs.formRef.offsetHeight;
        }

        if (this.appClients?.length) {
            this.clients = this.appClients;
        }
    },

    beforeUnmount() {
        // Clean up the observer when component is destroyed
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },

    methods: {
        addCCEmails() {
            this.clientCCEmails = this.client?.ccEmails || [];
        },

        removeCCEmails() {
            this.clientCCEmails = [];
        },

        onResize(entries) {
            for (const entry of entries) {
                // Update the form height
                this.formHeight = entry.target.offsetHeight;
            }
        },

        async handleClientsDropdown() {
            if (!this.clients.length) {
                await this.handleFetchClients();
            }
        },

        async handleFetchClients(name) {
            try {
                this.fetchClientsLoadingErr = false;
                this.fetchClientsLoading = true;

                const pageQueryObj = {
                    page: 1,
                    type: "all",
                    name: name || "",
                };
    
                const buildResult = assembleQueryList(pageQueryObj);
                this.clients = await this.$store.dispatch("clients/fetchClients", { params: buildResult, refresh: true, external: true });
            } catch(error) {
                this.fetchClientsLoadingErr = true;
            } finally {
                this.fetchClientsLoading = false;
            }
        },

        handleSelectClient(item) {
            this.client = item;
            
            if (typeof item.ccEmails === "string" && item.ccEmails) {
                this.clientCCEmails = item.ccEmails.split(",").map(email => email.trim());
            } else if (Array.isArray(item.ccEmails)) {
                this.clientCCEmails = item.ccEmails;
            } else {
                this.clientCCEmails = [];
            }
        },

        closeModal() {
            this.clientCCEmails = [];
            this.$emit("cancel");
        },

        setTemplate(val) {                        
            this.currentTemplate = val;
        },

        onComplete() {
            if (!this.client._id) {
                this.$toast.error("Select a client to contine");
                return;
            }

            this.$emit("submit", { 
                template: this.currentTemplate.name || "Template1.1",
                addCCEmails: !!this.clientCCEmails.length,
                clientId: this.client._id,
            });

            this.clientCCEmails = [];
            this.currentTemplate = {
                id: "template11", 
                name: "Template1.1", 
            };
            this.form = {
                noteValue: "",
            };
            this.client = {};
        },

        renderFilePath(name) {
            return require(`@/assets/img/rate-card-templates/${name}.png`);
        },

        prevStep() {
            this.step = this.step - 1;
            this.currentTemplate = this.downloadTemplates.at(this.step);
        },

        nextStep() {
            if (this.step >= this.downloadTemplates.length) {
                return;
            }
            this.step = this.step + 1;
            this.currentTemplate = this.downloadTemplates.at(this.step);
        },
    },
};
</script>

<style lang="scss" scoped>
.rc {
    &__template {
        &__item {
            // border: 1px solid black;
            // height: 150px;
            // width: 150px;

            &__img {
                border: 1px solid #ebebeb;
                // border: 1px solid red;
                border-radius: 10px;
                width: 100%;
                max-height: 80%;
                display: flex;
                padding: 5px;

                img {
                    width: 100%;
                    max-height: 100%;
                    object-fit: fill;
                }
            }

            &.active {
                .rc__template__item__img {
                    border: 1px solid var(--brand-600);
                    border: 2px dotted #878080;
                }
            }
            
            &__text {
                margin-top: 10px;
                
                p {
                    text-align: center;
                    font-size: 0.75rem;
                }
            }
        }

        &__list {
            grid-template-columns: 1fr 1fr 1fr;
            padding: 10px 20px;
        }
    }
}
</style>